import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Button from './Button';
import { Download } from 'lucide-react';
import logo from '../logo/logo.png';
import { Order, OrderItem } from '../services/order.service';

interface InvoiceDownloadProps {
  order: Order;
  variant?: 'primary' | 'secondary' | 'outline';
  className?: string;
}

const InvoiceDownload: React.FC<InvoiceDownloadProps> = ({ order, variant = 'outline', className = '' }) => {
  const generateInvoice = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // -- Logo Section --
    doc.addImage(logo, 'PNG', 20, 10, 25, 25);

    // -- Header Section --
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(34, 197, 94); // GreenGuest color
    doc.text('Green Guest', 50, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('Premium Indoor & Bonsai Plants', 50, 24);
    doc.text('Address: Beside - Masjidul Aman Complex, Pocket Gate,', 50, 29);
    doc.text('Vashantek, Dhaka cant, Dhaka 1206', 50, 34);
    doc.text('Phone: +8801817594685', 50, 39);

    // -- Invoice Info --
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0);
    doc.text('INVOICE', pageWidth - 60, 18);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${order.orderNumber || order.id}`, pageWidth - 60, 26);
    doc.text(`Date: ${new Date(order.createdAt).toLocaleDateString()}`, pageWidth - 60, 31);
    doc.text(`Status: ${order.status.toUpperCase()}`, pageWidth - 60, 36);

    // -- Billing Section --
    const billingY = 55;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, billingY);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`${order.firstName || ''} ${order.lastName || ''}`.trim() || 'N/A', 20, billingY + 6);
    doc.text(`Email: ${order.shippingEmail || 'N/A'}`, 20, billingY + 11);
    doc.text(`Phone: ${order.shippingPhone || 'N/A'}`, 20, billingY + 16);

    const addressLines = doc.splitTextToSize(order.shippingAddress || 'N/A', 80);
    doc.text('Address:', 20, billingY + 23);
    doc.text(addressLines, 20, billingY + 28);

    // -- Payment Info --
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Info:', pageWidth - 80, billingY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Method: ${order.paymentMethod || 'N/A'}`, pageWidth - 80, billingY + 6);

    // -- Table Section --
    const tableData = order.items.map((item, index) => {
      // Find the unit price in multiple possible locations (favoring discounted price if available)
      const rawPrice = (item as any).discountPrice ?? (item as any).product?.discountPrice ?? (item as any).price ?? (item as any).product?.price ?? 0;
      const unitPrice = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice as any) || 0;

      const subtotal = unitPrice * item.quantity;
      return [
        index + 1,
        item.productName,
        item.quantity,
        `Tk ${unitPrice.toFixed(2)}`,
        `Tk ${subtotal.toFixed(2)}`
      ];
    });

    autoTable(doc, {
      startY: 100,
      head: [['#', 'Product Name', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], textColor: 255 },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' }
      }
    });

    // -- Summary Section --
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary:', pageWidth - 80, finalY);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const totalWithShipping = typeof order.totalAmount === 'number' ? order.totalAmount : parseFloat(order.totalAmount as any) || 0;
    const shippingFee = typeof order.shippingFee === 'number' ? order.shippingFee : parseFloat(order.shippingFee as any) || 0;
    const subtotal = totalWithShipping - shippingFee;

    doc.text('Subtotal:', pageWidth - 80, finalY + 7);
    doc.text(`Tk ${subtotal.toFixed(2)}`, pageWidth - 20, finalY + 7, { align: 'right' });

    doc.text('Delivery Charge:', pageWidth - 80, finalY + 12);
    doc.text(`Tk ${shippingFee.toFixed(2)}`, pageWidth - 20, finalY + 12, { align: 'right' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94);
    doc.text('Grand Total:', pageWidth - 80, finalY + 21);
    doc.text(`Tk ${totalWithShipping.toFixed(2)}`, pageWidth - 20, finalY + 21, { align: 'right' });

    // -- Footer Section --
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for shopping with Green Guest BD!', pageWidth / 2, pageWidth === 210 ? 285 : 275, { align: 'center' });
    doc.text('This is a computer-generated invoice.', pageWidth / 2, pageWidth === 210 ? 290 : 280, { align: 'center' });

    // -- Download --
    doc.save(`Invoice_${order.orderNumber || order.id}.pdf`);
  };

  return (
    <Button
      variant={variant}
      onClick={generateInvoice}
      className={`flex items-center gap-2 ${className}`}
    >
      <Download className="h-4 w-4" />
      Download Invoice
    </Button>
  );
};

export default InvoiceDownload;
